import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Switch } from '@/components/ui/switch'
import { AnimatedSparkles } from '@/components/ui/animated-sparkles'
import { PhoneForwarded } from 'lucide-react'
import { cn } from '@/lib/utils'
import { STAFF_CONNECTION_METHOD_VALUES } from '@super/shared-types'
import type { Control, UseFormWatch } from 'react-hook-form'
import type { FormValues } from './agent-detail.schema'
import { VariableAutocompleteTextarea } from './VariableAutocompleteTextarea'

const [SIMPLE_TRANSFER, AI_TRANSFER] = STAFF_CONNECTION_METHOD_VALUES

type AgentAdvancedSettingsCardProps = {
  control: Control<FormValues>
  watch: UseFormWatch<FormValues>
  declinedMessageInputRef: React.MutableRefObject<
    HTMLInputElement | HTMLTextAreaElement | null
  >
  direction?: string
}

export function AgentAdvancedSettingsCard({
  control,
  watch,
  declinedMessageInputRef,
  direction,
}: AgentAdvancedSettingsCardProps) {
  const transferMethod = watch('staffConnectionMethod')
  const transferDisabled = !transferMethod
  const isAiTransfer = transferMethod === AI_TRANSFER
  const isOutbound = direction === 'OUTBOUND'

  return (
    <Card className="mb-40">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <PhoneForwarded className="w-4 h-4" />
          Live transfer
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <FormField
          control={control}
          name="staffConnectionMethod"
          render={({ field }) => (
            <FormItem className="space-y-3">
              <p className="text-sm font-bold">Transfer connection method</p>
              <label
                className={cn(
                  'flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors',
                  !field.value
                    ? 'border-primary/50 bg-primary/5'
                    : 'border-border hover:border-muted-foreground/40',
                )}
              >
                <input
                  type="radio"
                  name="staffConnectionMethod"
                  value=""
                  checked={!field.value}
                  onChange={() => field.onChange(null)}
                  className="mt-0.5"
                />
                <div>
                  <p className="text-sm font-medium">
                    Disabled{' '}
                    <span className="text-muted-foreground font-normal">
                      (default)
                    </span>
                  </p>
                  <p className="text-xs text-muted-foreground">
                    The agent handles the conversation end-to-end. No live
                    transfer to staff.
                  </p>
                </div>
              </label>
              {!isOutbound && (
                <div
                  className={cn(
                    'rounded-lg border transition-colors',
                    field.value === SIMPLE_TRANSFER
                      ? 'border-primary/50 bg-primary/5'
                      : 'border-border hover:border-muted-foreground/40',
                  )}
                >
                  <label className="flex items-start gap-3 p-3 cursor-pointer">
                    <input
                      type="radio"
                      name="staffConnectionMethod"
                      value={SIMPLE_TRANSFER}
                      checked={field.value === SIMPLE_TRANSFER}
                      onChange={() => field.onChange(SIMPLE_TRANSFER)}
                      className="mt-0.5"
                    />
                    <div>
                      <p className="text-sm font-medium">Simple transfer</p>
                      <p className="text-xs text-muted-foreground">
                        The agent conferences a live connection to staff member
                      </p>
                    </div>
                  </label>
                  {field.value === SIMPLE_TRANSFER && (
                    <FormField
                      control={control}
                      name="preserveOriginalCallerId"
                      render={({ field: preserveField }) => (
                        <div className="flex items-start justify-between gap-3 border-t border-border px-3 py-3">
                          <div className="space-y-1">
                            <p className="text-sm font-medium">
                              Preserve Original Caller ID
                            </p>
                            <p className="text-xs text-muted-foreground">
                              Shows the caller's number instead of the agent
                              number.
                            </p>
                          </div>
                          <FormControl>
                            <Switch
                              checked={preserveField.value}
                              onCheckedChange={preserveField.onChange}
                            />
                          </FormControl>
                        </div>
                      )}
                    />
                  )}
                </div>
              )}
              <label
                className={cn(
                  'flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors',
                  field.value === AI_TRANSFER
                    ? 'border-primary/50 bg-primary/5'
                    : 'border-border hover:border-muted-foreground/40',
                )}
              >
                <input
                  type="radio"
                  name="staffConnectionMethod"
                  value={AI_TRANSFER}
                  checked={field.value === AI_TRANSFER}
                  onChange={() => field.onChange(AI_TRANSFER)}
                  className="mt-0.5"
                />
                <div>
                  <p className="text-sm font-medium flex items-center gap-1.5">
                    <AnimatedSparkles
                      className="w-3.5 h-3.5"
                      animate={field.value === AI_TRANSFER}
                    />
                    AI phone tree transfer
                  </p>
                  <p className="text-xs text-muted-foreground">
                    The agent briefs about the conversation and asks for
                    approval to transfer
                  </p>
                </div>
              </label>
              <FormMessage />
            </FormItem>
          )}
        />

        <hr className="border-border" />

        <FormField
          control={control}
          name="transferInstructions"
          render={({ field }) => (
            <FormItem className={cn(transferDisabled && 'opacity-50')}>
              <FormLabel>Transfer conditions</FormLabel>
              <FormControl>
                <VariableAutocompleteTextarea
                  placeholder="Transfer calls when someone reports an urgent issue or asks for a representative"
                  className="min-h-[100px]"
                  disabled={transferDisabled}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="declinedMessage"
          render={({ field }) => {
            const { ref: fieldRef, ...fieldProps } = field
            return (
              <FormItem className={cn(!isAiTransfer && 'opacity-50')}>
                <FormLabel>Message when AI transfer is declined</FormLabel>
                <FormControl>
                  <Input
                    placeholder="We weren't able to connect you to a staff member. Can you leave us a brief message ?"
                    ref={e => {
                      fieldRef(e)
                      declinedMessageInputRef.current = e
                    }}
                    {...fieldProps}
                    value={fieldProps.value ?? ''}
                    disabled={!isAiTransfer}
                  />
                </FormControl>
                <FormDescription>
                  Spoken by the agent when all staff members decline or
                  don&apos;t answer the transfer attempt.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )
          }}
        />
      </CardContent>
    </Card>
  )
}
